import { FC } from 'react';
import { Button, Space, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  APPOINTMENT_STATUSES,
  APPOINTMENT_STATUS_VALUES
} from 'constants/appointments';
import { encodeToBase64, getIdStart } from '@medicusunion/front-utils';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { Link } from 'react-router-dom';
import { EyeOutlined } from '@ant-design/icons';
import { PLAN_TYPE_VALUES } from 'constants/profile';
import { useUIDispatchContext } from 'providers/UI';
import { openRoom as openRoomRest } from 'services/api/appointments';

import { showRestError } from 'utils/showErrorMessage';
import { getStatusColor } from 'utils/appointments';
import { ActionMenu } from 'components/ui';
import { ActionMenuItem } from 'components/ui/ActionMenu/types';
import { tokenStorage, refreshTokenStorage } from 'utils/storage';

interface IProps {
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dates: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  plan: any;
  paid: boolean;
  status: number;
}

const AppointmentItem: FC<IProps> = ({ id, dates, plan, paid, status }) => {
  const { t, i18n } = useTranslation();
  const { toggleModal } = useUIDispatchContext();

  const openRoom = (appointmentId: string) => {
    openRoomRest(appointmentId)
      .then(res => {
        const token = encodeToBase64(tokenStorage.get());
        const refreshToken = encodeToBase64(refreshTokenStorage.get());

        if (res.redirect_url) {
          const link = `${res.redirect_url}/home/${res.meetUrl}?t=${token}&r=${refreshToken}&lang=${i18n.language}`;

          window.location.href = link;
        }
      })
      .catch(err => showRestError(err));
  };

  const dateItem = dates.find(item => item.accepted);
  const actions: ActionMenuItem[] = [];

  const isVideoCall = plan?.type === PLAN_TYPE_VALUES.video_call;

  const showJoinCall =
    isVideoCall &&
    paid &&
    (status === APPOINTMENT_STATUSES.approved ||
      status === APPOINTMENT_STATUSES.started);

  if (showJoinCall && dateItem?.date) {
    const diff = dayjs(dateItem.date).diff(dayjs(), 'seconds');

    if (diff <= 15 * 60) {
      actions.push({
        title: t('patient.join_call'),
        action() {
          openRoom(id);
        }
      });
    }
  }

  const showStatusActions =
    status === APPOINTMENT_STATUSES.awaiting_confirmation;

  if (showStatusActions) {
    actions.push({
      title: t('patient.reject_appointment'),
      action() {
        toggleModal('rejectAppointment', true, {
          id,
          status: APPOINTMENT_STATUSES.rejected
        });
      }
    });
  }

  return (
    <>
      <p>
        <span className="list-content-item-title">
          <b>ID #{getIdStart(id)}</b>
        </span>
        <Tag color={getStatusColor(status)}>
          {t(`appointments.${APPOINTMENT_STATUS_VALUES[status]}`)}
        </Tag>
      </p>
      <p>
        <span className="list-content-item-title">
          {t('patient.appointment_date')}
        </span>
        <span className="list-content-item-value">
          {dateItem?.date ? dayjs(dateItem.date).format(FULL_DATE_FORMAT) : '-'}
        </span>
      </p>
      <p>
        <span className="list-content-item-title">{t('patient.payment')}</span>
        <span className="list-content-item-value">
          {paid ? t('patient.paid') : t('patient.not_paid')}
        </span>
      </p>
      <Space size={16}>
        <Link to={`/appointments/details/${id}`}>
          <Button
            icon={<EyeOutlined />}
            type="primary"
            ghost
            size="large"
            data-element="patient-view-appointment"
          >
            {t('patient.view_appointment')}
          </Button>
        </Link>
        {actions.length ? (
          <div data-element="patient-actions-appointment">
            <ActionMenu data={actions} />
          </div>
        ) : (
          <div style={{ width: 46 }} />
        )}
      </Space>
    </>
  );
};

export default AppointmentItem;
